import { EmptyState } from 'module/ui/components/atoms/empty-state';
import { faFileText, faVideo } from '@fortawesome/pro-solid-svg-icons';
import {
  Drawer,
  DrawerBody,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Icon,
  Loader,
} from '@rocketlane/ui';
import moment from 'moment';
import { useMeetingTranscriptQuery } from 'queries/meeting';
import { useGetMeetingPagesForCompanyQuery } from 'queries/pages';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { apiTranscriptToUiTranscript } from './api-transcript-ui-transcript';
import { JumpToCurrentTime } from './jump-to-current-time';
import { VideoPlayer } from './player/player';
import { useVideoPlayerContext, VideoPlayerProvider } from './player-context';
import { useGetMeetingRecordingWithRetry } from './recording-with-retry';
import { SignalNavigator } from './signal-navigator';
import { useSyncTranscriptWithPlayer } from './sync-transcript-with-player';
import { Transcript } from './transcript';
import { useDetectUserScroll } from './user-scroll-detector';
import { getMeetingPageRoute } from '../meeting-pages/route-navigations';

export type MeetingTranscriptDrawerState = {
  meetingId: number;
  source?: {
    companyId: number;
    meetingName: string;
    startTime: string;
  };
  timeToSeek?: number;
} | null;
type DrawerProps = {
  drawerState: MeetingTranscriptDrawerState;
  onClose: () => void;
  projectId?: number;
};

const signalOccurrencesForMeetingWithTimePeriod = [];

const MeetingTranscriptDrawerContent = ({
  meetingId,
  timeToSeek,
}: {
  meetingId: number;
  timeToSeek?: number;
}) => {
  const { data: transcript, isLoading: isMeetingTranscriptLoading } =
    useMeetingTranscriptQuery(meetingId);
  const {
    meetingRecording,
    isMeetingRecordingLoading,
    refetchMeetingRecordingWithRetry,
  } = useGetMeetingRecordingWithRetry({
    meetingId,
  });
  const isLoading = isMeetingTranscriptLoading || isMeetingRecordingLoading;

  const uiTranscript = useMemo(() => {
    if (transcript) {
      return apiTranscriptToUiTranscript(transcript);
    }

    return undefined;
  }, [transcript]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { canPlay } = useVideoPlayerContext();
  const { isAutoScrollEnabled, startProgrammaticScroll, enableAutoScroll } =
    useDetectUserScroll(scrollContainerRef, canPlay);
  const { activeSegment, activeChunk, scrollToSegment } =
    useSyncTranscriptWithPlayer({
      transcript: transcript ?? { segments: [] },
      isAutoScrollEnabled: canPlay && isAutoScrollEnabled,
      beforeScrollToSegment: startProgrammaticScroll,
    });
  const enableAutoScrollToSegment = useCallback(() => {
    enableAutoScroll();
    if (activeSegment) {
      scrollToSegment(activeSegment);
    } else if (transcript != null) {
      scrollToSegment(transcript.segments[0]);
    }
  }, [activeSegment, enableAutoScroll, scrollToSegment, transcript]);
  const { moveToTime } = useVideoPlayerContext();

  useEffect(() => {
    if (timeToSeek != null) {
      moveToTime(timeToSeek);
    }
  }, [timeToSeek, moveToTime]);

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col h-full">
          {meetingRecording?.videoUrl == null && transcript == null ? (
            <div className="flex justify-center items-center h-full py-05 px-06">
              <EmptyState
                image={<Icon icon={faFileText} size={'2x'} />}
                message={
                  <FormattedMessage id="account.meeting_transcript.no_transcript_or_video" />
                }
              />
            </div>
          ) : (
            <>
              <div className="flex-1 py-05 px-06">
                {meetingRecording?.videoUrl != null ? (
                  <VideoPlayer
                    videoSrc={meetingRecording.videoUrl}
                    transcript={uiTranscript}
                    onError={refetchMeetingRecordingWithRetry}
                  />
                ) : (
                  <div className="flex justify-center items-center h-full aspect-video">
                    <EmptyState
                      image={<Icon icon={faVideo} size={'2x'} />}
                      message={
                        <FormattedMessage id="account.meeting_transcript.video.not_available" />
                      }
                    />
                  </div>
                )}
              </div>
              {transcript ? (
                <div className="flex p-05 h-full w-full relative overflow-hidden">
                  <div
                    ref={scrollContainerRef}
                    className="flex flex-col overflow-y-auto h-full"
                  >
                    <Transcript
                      activeSegment={activeSegment}
                      activeChunk={activeChunk}
                      transcript={transcript}
                      signalOccurrencesForMeetingWithTimePeriod={
                        signalOccurrencesForMeetingWithTimePeriod
                      }
                    />
                  </div>
                  <SignalNavigator
                    signalOccurrencesForMeetingWithTimePeriod={
                      signalOccurrencesForMeetingWithTimePeriod
                    }
                    triggerClassName="min-w-[0px]"
                  />
                  {canPlay && (
                    <JumpToCurrentTime
                      isAutoScrollEnabled={isAutoScrollEnabled}
                      enableAutoScrollToSegment={enableAutoScrollToSegment}
                    />
                  )}
                </div>
              ) : (
                <div className="flex p-05 gap-05 h-full w-full relative overflow-hidden justify-center">
                  <EmptyState
                    image={<Icon icon={faFileText} size={'2x'} />}
                    message={
                      <FormattedMessage id="account.meeting_transcript.no_transcript" />
                    }
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export const MeetingTranscriptDrawer = ({
  drawerState,
  onClose,
  projectId,
}: DrawerProps) => {
  const { data: meetingDocuments, isLoading: isMeetingDocumentsLoading } =
    useGetMeetingPagesForCompanyQuery(drawerState?.source?.companyId ?? -1, {
      enabled: drawerState?.source?.companyId !== undefined,
    });
  const currentDocument = useMemo(() => {
    return meetingDocuments?.find(
      (document) => document.sourceId === drawerState?.meetingId,
    );
  }, [meetingDocuments, drawerState?.meetingId]);
  const hasValidMeetingDocument = useMemo(() => {
    return currentDocument != null;
  }, [currentDocument]);

  const openMeetingPage = useCallback(() => {
    if (hasValidMeetingDocument) {
      window.open(
        getMeetingPageRoute(
          drawerState?.source?.companyId ?? -1,
          currentDocument?.documentId ?? -1,
          projectId,
        ),
        '_blank',
      );
    }
  }, [
    hasValidMeetingDocument,
    drawerState?.source?.companyId,
    currentDocument,
    projectId,
  ]);

  return (
    <VideoPlayerProvider>
      <Drawer
        size="488px"
        placement="right"
        open={!!drawerState}
        onClose={onClose}
      >
        {isMeetingDocumentsLoading ? (
          <>
            <DrawerTitle className="sr-only">
              <FormattedMessage id="account.meeting_transcript.drawer.title" />
            </DrawerTitle>
            <div className="flex justify-center items-center h-full">
              <Loader />
            </div>
          </>
        ) : (
          <>
            <DrawerHeader>
              <DrawerTitle>
                <div
                  className={` ${
                    hasValidMeetingDocument
                      ? 'hover:underline cursor-pointer'
                      : ''
                  } text-ellipsis overflow-hidden whitespace-nowrap max-w-[calc(100%-40px)]`}
                  onClick={openMeetingPage}
                >
                  {currentDocument?.title ?? (
                    <FormattedMessage id="account.meeting_transcript.drawer.title" />
                  )}
                </div>
              </DrawerTitle>
              {drawerState?.source?.startTime ? (
                <DrawerDescription>
                  {moment(drawerState.source.startTime).format('D MMM, h:mm A')}
                </DrawerDescription>
              ) : null}
            </DrawerHeader>
            <DrawerBody className="!p-0">
              {drawerState && (
                <MeetingTranscriptDrawerContent
                  meetingId={drawerState.meetingId}
                  timeToSeek={drawerState.timeToSeek}
                />
              )}
            </DrawerBody>
          </>
        )}
      </Drawer>
    </VideoPlayerProvider>
  );
};
